import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

/**
 * Standard auth form field. Label above, input in the middle, helper
 * text below the input. When `error` is non-empty, the helper line
 * is replaced by the error message and the input gets a red border.
 *
 * Accessibility:
 *  - `<label htmlFor={id}>` always associated with the input.
 *  - `aria-describedby` points at the helper or error element so
 *    screen readers announce it when the field has focus.
 *  - `aria-invalid` is set when an error is present.
 *
 * Pass `inputRef` to forward a ref onto the underlying <input>; useful
 * for auto-focus on mount.
 */
type Props = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  helper?: ReactNode;
  error?: string;
  /** Extra classes for the outer wrapper <div>. */
  wrapperClassName?: string;
  /** Override the rendered field — used by PasswordInput and OtpInput. */
  renderInput?: (props: { id: string; describedBy: string; invalid: boolean }) => ReactNode;
};

export function FormField({
  label,
  helper,
  error,
  renderInput,
  wrapperClassName,
  className,
  ...inputProps
}: Props) {
  const reactId = useId();
  const id = inputProps.id ?? `f-${reactId}`;
  const describedBy = `${id}-msg`;
  const invalid = !!error;

  return (
    <div className={wrapperClassName}>
      <label htmlFor={id} className="block text-sm font-medium text-ink mb-1.5">
        {label}
      </label>

      {renderInput ? (
        renderInput({ id, describedBy, invalid })
      ) : (
        <input
          {...inputProps}
          id={id}
          aria-describedby={helper || error ? describedBy : undefined}
          aria-invalid={invalid || undefined}
          className={
            'input-base ' +
            (invalid ? 'border-semantic-error focus:border-semantic-error ' : '') +
            (className ?? '')
          }
        />
      )}

      {(helper || error) && (
        <p
          id={describedBy}
          className={'mt-1.5 text-xs ' + (invalid ? 'text-semantic-error' : 'text-ink-tertiary')}
        >
          {error || helper}
        </p>
      )}
    </div>
  );
}
